import { useCallback, useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import HardBreak from '@tiptap/extension-hard-break';
import Text from '@tiptap/extension-text';
import LinkExtension from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Markdown } from 'tiptap-markdown';

import { EditorToolbar } from './EditorToolbar';
import WidgetMarkdownPreview from './WidgetMarkdownPreview';
import {
  StarRatingDialog,
  CTADialog,
  LogoDialog,
  MediaCarouselDialog,
  ItemCarouselDialog,
  FormDialog,
  SingleFormDialog,
  InteractiveFlowDialog,
  GoogleReviewsDialog,
  TableDialog,
} from './InsertDialogs';

export interface RichTextEditorRef {
  insertRawText: (text: string) => void;
}

export interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  minHeight?: string;
  /** Kept for backward compat — no longer filters insert options (all are always shown) */
  enabledInserts?: string[];
  onInsertAsset?: () => void;
  onInsertCarousel?: () => void;
  /** When true, hides the entire toolbar */
  hideToolbar?: boolean;
  /** When true, the editor starts in preview mode */
  defaultPreviewMode?: boolean;
  /**
   * true (default) — editing is in raw markdown.
   * false — editing is in the visual editor, which writes the markdown the widget reads (see
   *         VISUAL_EXTENSIONS). Content it can't keep as it is opens in raw markdown instead.
   */
  defaultRawMode?: boolean;
  /** Accessible name for the visual editor (it has no <label> of its own). */
  ariaLabel?: string;
  /** Container height – defaults to '400px'. Use 'auto' for content-driven height. */
  height?: string;
  /** When true, disables all editing — toolbar formatting buttons are disabled, textarea and rich editor are read-only. The Raw/Preview toggle still works. */
  readOnly?: boolean;
  /**
   * 'full' (default) — all toolbar buttons.
   * 'minimal' — Bold/Italic/Strike, Headings, Link and Raw/Preview only.
   *             Undo/Redo, Lists, Blockquote, Code and Insert are hidden.
   * 'basic'   — text formatting only: everything but Image and Insert (widget components).
   */
  toolbarVariant?: 'full' | 'minimal' | 'basic';
}

type DialogKey =
  | 'starRating'
  | 'cta'
  | 'logo'
  | 'mediaCarousel'
  | 'itemCarousel'
  | 'form'
  | 'singleForm'
  | 'interactiveFlow'
  | 'googleReviews'
  | 'table'
  | null;

// Widget-specific XML tags that tiptap cannot round-trip safely.
// Content containing these must always stay in the raw string path and never
// pass through tiptap's parser/serializer.
const WIDGET_TAG_RE = /<(carousel|media-item|item|interactive-flow|form|field|single-form|RandomGoogleReviews)/i;

// The visual editor's markdown, as the widget reads it. The widget doesn't read markdown escapes
// (`file\_name` shows its backslash) or backslash line breaks, and it shows a single newline as a
// line break. So text is written as typed, a line break (Shift+Enter) is a newline, a newline is
// read back as a line break, and "<" is text.
const PlainText = Text.extend({
  addStorage() {
    return {
      markdown: {
        serialize(state: { text: (text: string, escape?: boolean) => void }, node: { text: string }) {
          state.text(node.text, false);
        },
        parse: {},
      },
    };
  },
});

const NewlineBreak = HardBreak.extend({
  addStorage() {
    return {
      markdown: {
        // As tiptap-markdown's own: written only when content follows it in the block.
        serialize(
          state: { write: (content: string) => void },
          node: { type: unknown },
          parent: { childCount: number; child: (index: number) => { type: unknown } },
          index: number
        ) {
          for (let i = index + 1; i < parent.childCount; i++) {
            if (parent.child(i).type !== node.type) {
              state.write('\n');
              return;
            }
          }
        },
        parse: {},
      },
    };
  },
});

/**
 * Content the visual editor keeps as it is. Widget components and images (it has no image node)
 * would be lost, and runs of blank lines collapsed; the onUpdate guards would then skip every
 * edit, so such content is edited in raw markdown.
 */
const keepsInVisualEditor = (markdown: string) =>
  !WIDGET_TAG_RE.test(markdown) && !markdown.includes('![') && !/\n{3,}/.test(markdown);

export const RichTextEditor = forwardRef<RichTextEditorRef, RichTextEditorProps>(({
  value,
  onChange,
  placeholder = 'Start typing...',
  minHeight = '200px',
  onInsertAsset,
  hideToolbar = false,
  defaultPreviewMode = false,
  defaultRawMode = true,
  ariaLabel,
  height = '400px',
  readOnly = false,
  toolbarVariant = 'full',
}, ref) => {
  const visual = !defaultRawMode;
  const [openDialog, setOpenDialog] = useState<DialogKey>(null);
  const [isRawMode, setIsRawMode] = useState(!defaultPreviewMode && (defaultRawMode || !keepsInVisualEditor(value)));
  const [isPreviewMode, setIsPreviewMode] = useState(defaultPreviewMode);
  const [rawCursorPos, setRawCursorPos] = useState(0);
  const [rawSelEnd, setRawSelEnd] = useState(0);
  const [canRawUndo, setCanRawUndo] = useState(false);
  const [canRawRedo, setCanRawRedo] = useState(false);
  const isInternalChange = useRef(false);
  const isSwitchingToRich = useRef(false);
  const lastExternalValue = useRef(value);
  const rawTextareaRef = useRef<HTMLTextAreaElement>(null);
  // Ref-based mirror of isRawMode so the tiptap onUpdate closure can read the
  // current mode without being stale (closures capture the value at creation time).
  const isRawModeRef = useRef(isRawMode);
  // Keep isRawModeRef in sync whenever isRawMode state changes.
  useEffect(() => { isRawModeRef.current = isRawMode; }, [isRawMode]);

  // ── Raw-mode undo/redo history ──
  interface RawSnapshot { value: string; selStart: number; selEnd: number; }
  const rawUndoStack = useRef<RawSnapshot[]>([]);
  const rawRedoStack = useRef<RawSnapshot[]>([]);
  // Snapshot of the state BEFORE the latest change (pushed to undo on the next change)
  const rawPendingSnapshot = useRef<RawSnapshot>({ value, selStart: 0, selEnd: 0 });
  const rawDebounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const pushRawHistory = (prevSnapshot: RawSnapshot) => {
    rawUndoStack.current.push(prevSnapshot);
    rawRedoStack.current = [];
    setCanRawUndo(true);
    setCanRawRedo(false);
  };

  const rawUndo = () => {
    const ta = rawTextareaRef.current;
    const prev = rawUndoStack.current.pop();
    if (!prev) return;
    // Push current state to redo
    rawRedoStack.current.push({
      value,
      selStart: ta?.selectionStart ?? 0,
      selEnd: ta?.selectionEnd ?? 0,
    });
    rawPendingSnapshot.current = prev;
    isInternalChange.current = true;
    lastExternalValue.current = prev.value;
    onChange(prev.value);
    requestAnimationFrame(() => {
      const t = rawTextareaRef.current;
      if (t) {
        t.selectionStart = prev.selStart;
        t.selectionEnd = prev.selEnd;
        t.focus();
        setRawCursorPos(prev.selStart);
        setRawSelEnd(prev.selEnd);
      }
    });
    setCanRawUndo(rawUndoStack.current.length > 0);
    setCanRawRedo(true);
  };

  const rawRedo = () => {
    const ta = rawTextareaRef.current;
    const next = rawRedoStack.current.pop();
    if (!next) return;
    rawUndoStack.current.push({
      value,
      selStart: ta?.selectionStart ?? 0,
      selEnd: ta?.selectionEnd ?? 0,
    });
    rawPendingSnapshot.current = next;
    isInternalChange.current = true;
    lastExternalValue.current = next.value;
    onChange(next.value);
    requestAnimationFrame(() => {
      const t = rawTextareaRef.current;
      if (t) {
        t.selectionStart = next.selStart;
        t.selectionEnd = next.selEnd;
        t.focus();
        setRawCursorPos(next.selStart);
        setRawSelEnd(next.selEnd);
      }
    });
    setCanRawUndo(true);
    setCanRawRedo(rawRedoStack.current.length > 0);
  };

  /** Called before every raw value change to record the "before" snapshot. */
  const recordRawChange = () => {
    if (rawDebounceTimer.current) clearTimeout(rawDebounceTimer.current);
    const snapshot = rawPendingSnapshot.current;
    rawDebounceTimer.current = setTimeout(() => {
      pushRawHistory(snapshot);
      rawPendingSnapshot.current = {
        value,
        selStart: rawTextareaRef.current?.selectionStart ?? 0,
        selEnd: rawTextareaRef.current?.selectionEnd ?? 0,
      };
    }, 300);
  };

  const updateRawCursor = () => {
    const ta = rawTextareaRef.current;
    if (ta) {
      setRawCursorPos(ta.selectionStart);
      setRawSelEnd(ta.selectionEnd);
    }
  };

  const editor = useEditor({
    extensions: [
      ...(visual ? [StarterKit.configure({ text: false, hardBreak: false }), PlainText, NewlineBreak] : [StarterKit]),
      LinkExtension.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-indigo-600 underline hover:text-indigo-800',
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
      Markdown.configure({
        html: !visual,
        breaks: visual,
        transformPastedText: true,
        transformCopiedText: true,
      }),
    ],
    content: value,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      if (readOnly) return;
      // Suppress onChange during a programmatic setContent (e.g. raw→rich mode switch).
      if (isSwitchingToRich.current) return;
      // ─── Raw-mode guard ──────────────────────────────────────────────────────
      // When the user is in raw mode the textarea owns the authoritative value.
      // Tiptap's onUpdate fires on every setOptions() call (including React
      // re-renders) even when the user hasn't typed anything, so we must never
      // let it overwrite the raw value — especially on initial mount where
      // tiptap-markdown collapses multiple blank lines into a single \n\n.
      if (isRawModeRef.current) return;
      // ─────────────────────────────────────────────────────────────────────────
      const storage = editor.storage as any;
      const markdown = storage.markdown?.getMarkdown?.() ?? editor.getHTML();
      // ─── Semantic widget-markup guard ────────────────────────────────────────
      // @tiptap/react calls editor.setOptions() on every React render, which
      // dispatches an empty ProseMirror transaction and fires onUpdate — even
      // when nothing the user typed changed.  When tiptap's serialized output has
      // dropped widget-specific XML that the parent's value still contains, it
      // means tiptap mangled the content.  Skip this update unconditionally so the
      // parent's authoritative value is never overwritten with mangled content.
      // This guard is timing-independent and covers all trigger sources
      // (internal re-renders, mode switches, paste, etc.).
      if (WIDGET_TAG_RE.test(lastExternalValue.current) && !WIDGET_TAG_RE.test(markdown)) return;
      // Protect plain markdown images: tiptap drops ![alt](url) when there is no
      // Image extension in the schema. Without this guard the image would silently
      // disappear from the parent's value every time onUpdate fires.
      if (lastExternalValue.current.includes('![') && !markdown.includes('![')) return;
      // ─── Blank-line preservation guard ──────────────────────────────────────
      // tiptap-markdown's ProseMirror serializer normalises multiple consecutive
      // blank lines down to a single \n\n (paragraph boundary).  If the external
      // value had more blank-line groups than what tiptap serialised, skip the
      // update so we don't silently collapse the user's whitespace.
      const countBlankLineGroups = (s: string) => (s.match(/\n{3,}/g) ?? []).length;
      if (countBlankLineGroups(lastExternalValue.current) > countBlankLineGroups(markdown)) return;
      // ─────────────────────────────────────────────────────────────────────────
      isInternalChange.current = true;
      lastExternalValue.current = markdown;
      onChange(markdown);
    },
    editorProps: {
      attributes: {
        class: 'rte-content prose prose-sm max-w-none focus:outline-none px-4 py-3',
        style: `min-height: ${minHeight}`,
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
  });

  // Keep tiptap editable state in sync with readOnly prop. No update event: nothing was edited,
  // and the visual editor would otherwise write its own markdown back the moment it opens.
  useEffect(() => {
    if (editor) editor.setEditable(!readOnly, false);
  }, [editor, readOnly]);

  // Sync external value changes into tiptap.
  // Guard with isSwitchingToRich so tiptap's onUpdate callback never overwrites
  // the parent's authoritative value with its mangled interpretation of
  // widget-specific XML (carousel, form, interactive-flow, etc.).
  useEffect(() => {
    if (!editor || isInternalChange.current) {
      isInternalChange.current = false;
      return;
    }
    if (value !== lastExternalValue.current) {
      lastExternalValue.current = value;
      // New content (a form reset): the visual editor takes it if it can keep it as it is.
      if (visual && !isPreviewMode) setIsRawMode(!keepsInVisualEditor(value));
      isSwitchingToRich.current = true;
      editor.commands.setContent(value);
      requestAnimationFrame(() => {
        isSwitchingToRich.current = false;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  /**
   * Insert raw widget-compatible text at the current cursor position.
   * Works in both rich and raw modes.
   */
  const insertRawText = useCallback(
    (raw: string) => {
      // If the content contains widget-specific XML (carousel, form, interactive-flow …)
      // we MUST bypass tiptap entirely — it will strip or mangle the custom tags.
      // When in rich mode we flush the current tiptap content first, then switch to
      // raw mode so the user can see the inserted markup.
      const hasWidgetMarkup = WIDGET_TAG_RE.test(raw);

      if (isRawMode || hasWidgetMarkup) {
        isInternalChange.current = true;

        if (!isRawMode && hasWidgetMarkup) {
          // Rich mode: flush any typed content from tiptap, then switch to raw mode
          let baseValue = value;
          if (editor) {
            const storage = editor.storage as any;
            const richContent = storage.markdown?.getMarkdown?.() ?? editor.getHTML();
            if (richContent) baseValue = richContent;
          }
          const sep = baseValue && !baseValue.endsWith('\n') ? '\n' : '';
          const newValue = baseValue + sep + raw;
          lastExternalValue.current = newValue;
          onChange(newValue);
          setIsRawMode(true); // auto-switch so the user sees the raw carousel markup
          return;
        }

        // Raw mode: insert at cursor position
        const textarea = rawTextareaRef.current;
        if (textarea) {
          const start = textarea.selectionStart;
          const end = textarea.selectionEnd;
          const newValue = value.substring(0, start) + raw + value.substring(end);
          lastExternalValue.current = newValue;
          onChange(newValue);
          // Restore cursor position after React re-render
          requestAnimationFrame(() => {
            textarea.selectionStart = textarea.selectionEnd = start + raw.length;
            textarea.focus();
          });
        } else {
          const sep = value && !value.endsWith('\n') ? '\n' : '';
          const newValue = value + sep + raw;
          lastExternalValue.current = newValue;
          onChange(newValue);
        }
      } else {
        if (!editor) return;
        editor.chain().focus().insertContent(raw).run();
      }
    },
    [editor, isRawMode, value, onChange]
  );

  // Expose insertRawText via ref so parent components can insert text
  useImperativeHandle(ref, () => ({
    insertRawText: (text: string) => insertRawText(text),
  }), [insertRawText]);

  const handleDialogInsert = useCallback(
    (raw: string) => {
      insertRawText(raw);
      setOpenDialog(null);
    },
    [insertRawText]
  );

  /**
   * Toggle between raw and rich modes, syncing content.
   */
  const handleToggleRawMode = useCallback(() => {
    if (isRawMode) {
      // Switching raw → rich: push current value into tiptap.
      // Set isSwitchingToRich so the onUpdate handler won't call onChange — we don't want
      // tiptap's interpretation of widget-specific XML (carousel, form, etc.) to
      // overwrite the authoritative raw value held by the parent.
      if (editor) {
        isSwitchingToRich.current = true;
        isInternalChange.current = true;
        lastExternalValue.current = value;
        editor.commands.setContent(value);
        // Clear the flag after tiptap has finished processing (next animation frame is enough)
        requestAnimationFrame(() => {
          isSwitchingToRich.current = false;
        });
      }
    }
    // Switching rich → raw: `value` is already kept in sync by onUpdate on every
    // tiptap keystroke, so there is no need to re-read getMarkdown() here.
    // Re-reading would actually mangle any widget-specific XML that tiptap parsed.
    setIsRawMode(!isRawMode);
  }, [isRawMode, editor, value]);

  /** Toggle preview mode: renders markdown as the widget does (see WidgetMarkdownPreview). */
  const handleTogglePreview = useCallback(() => {
    setIsPreviewMode((prev) => {
      if (!prev) {
        // Entering preview: blur any focused element to remove focus ring immediately
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
      }
      return !prev;
    });
  }, []);

  return (
    <div className={`border border-gray-300  overflow-hidden ${!isPreviewMode ? 'focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500' : ''}`}>
      {!hideToolbar && (
        <EditorToolbar
          editor={editor}
          onOpenDialog={(key) => setOpenDialog(key)}
          onInsertRaw={insertRawText}
          onInsertAsset={onInsertAsset}
          isRawMode={isRawMode}
          onToggleRawMode={handleToggleRawMode}
          isPreviewMode={isPreviewMode}
          onTogglePreview={handleTogglePreview}
          readOnly={readOnly}
          rawTextareaRef={rawTextareaRef}
          rawValue={value}
          onRawChange={(newVal: string) => {
            recordRawChange();
            isInternalChange.current = true;
            lastExternalValue.current = newVal;
            onChange(newVal);
            // Flush selection state after raw actions' requestAnimationFrame has settled
            setTimeout(updateRawCursor, 0);
          }}
          rawCursorPos={rawCursorPos}
          rawSelEnd={rawSelEnd}
          onRawUndo={rawUndo}
          onRawRedo={rawRedo}
          canRawUndo={canRawUndo}
          canRawRedo={canRawRedo}
          toolbarVariant={toolbarVariant}
        />
      )}
      <div
        style={{
          height,
          minHeight,
          ...(height !== 'auto' ? { resize: 'vertical' as const } : {}),
          overflow: 'auto',
        }}
      >
        {isPreviewMode ? (
          <div className="w-full h-full p-4 bg-white overflow-auto">
            <WidgetMarkdownPreview value={value} />
          </div>
        ) : isRawMode ? (
          <textarea
            ref={rawTextareaRef}
            value={value}
            onChange={(e) => {
              recordRawChange();
              isInternalChange.current = true;
              lastExternalValue.current = e.target.value;
              onChange(e.target.value);
              setRawCursorPos(e.target.selectionStart);
            }}
            onKeyDown={(e) => {
              if (e.key !== 'Enter' && e.key !== 'Backspace') return;
              const ta = e.currentTarget;
              if (ta.selectionStart !== ta.selectionEnd) return; // has selection — let default handle

              const pos = ta.selectionStart;
              const v = value;
              const lineStart = v.lastIndexOf('\n', pos - 1) + 1;
              const lineEndRaw = v.indexOf('\n', pos);
              const lineEnd = lineEndRaw === -1 ? v.length : lineEndRaw;
              const line = v.substring(lineStart, lineEnd);

              // Detect list prefix
              const bulletMatch = line.match(/^([-*+] )/);
              const orderedMatch = line.match(/^(\d+)\. /);

              let prefix = '';
              let nextPrefix = '';
              if (bulletMatch) {
                prefix = bulletMatch[1];
                nextPrefix = bulletMatch[1];
              } else if (orderedMatch) {
                const n = parseInt(orderedMatch[1], 10);
                prefix = `${n}. `;
                nextPrefix = `${n + 1}. `;
              }

              // Not a list line, or cursor is inside the prefix itself — let default handle
              if (!prefix || pos < lineStart + prefix.length) return;

              const contentStart = lineStart + prefix.length;
              const lineContent = line.substring(prefix.length);

              const applyChange = (newValue: string, newCursor: number) => {
                recordRawChange();
                isInternalChange.current = true;
                lastExternalValue.current = newValue;
                onChange(newValue);
                requestAnimationFrame(() => {
                  ta.selectionStart = ta.selectionEnd = newCursor;
                  ta.focus();
                  setRawCursorPos(newCursor);
                  setRawSelEnd(newCursor);
                });
              };

              if (e.key === 'Enter') {
                e.preventDefault();
                if (lineContent.trim() === '') {
                  // Empty list item → exit list (remove this line's prefix, cursor on clean line)
                  const lineEndIncl = lineEndRaw !== -1 ? lineEndRaw + 1 : v.length;
                  const newValue = v.substring(0, lineStart) + v.substring(lineEndIncl);
                  applyChange(newValue, lineStart);
                } else {
                  // Continue list: split at cursor, move text after cursor to new item
                  const afterCursor = v.substring(pos, lineEnd);
                  const insertion = '\n' + nextPrefix + afterCursor;
                  const newValue = v.substring(0, pos) + insertion + v.substring(lineEnd);
                  applyChange(newValue, pos + 1 + nextPrefix.length);
                }
              } else if (e.key === 'Backspace' && pos === contentStart && lineContent === '') {
                // Empty list item → Backspace removes the prefix
                e.preventDefault();
                const newValue = v.substring(0, lineStart) + v.substring(contentStart);
                applyChange(newValue, lineStart);
              }
            }}
            onKeyUp={updateRawCursor}
            onMouseUp={updateRawCursor}
            onSelect={updateRawCursor}
            readOnly={readOnly}
            placeholder={placeholder}
            className={`w-full h-full px-4 py-3 text-sm font-mono text-gray-800 resize-none focus:outline-none ${readOnly ? 'bg-white cursor-default select-text' : 'bg-gray-50'}`}
            spellCheck={false}
          />
        ) : (
          <EditorContent editor={editor} />
        )}
      </div>

      {/* ─── All Insert Dialogs ─── */}
      <StarRatingDialog
        isOpen={openDialog === 'starRating'}
        onClose={() => setOpenDialog(null)}
        onInsert={handleDialogInsert}
      />
      <CTADialog
        isOpen={openDialog === 'cta'}
        onClose={() => setOpenDialog(null)}
        onInsert={handleDialogInsert}
      />
      <LogoDialog
        isOpen={openDialog === 'logo'}
        onClose={() => setOpenDialog(null)}
        onInsert={handleDialogInsert}
      />
      <MediaCarouselDialog
        isOpen={openDialog === 'mediaCarousel'}
        onClose={() => setOpenDialog(null)}
        onInsert={handleDialogInsert}
      />
      <ItemCarouselDialog
        isOpen={openDialog === 'itemCarousel'}
        onClose={() => setOpenDialog(null)}
        onInsert={handleDialogInsert}
      />
      <FormDialog
        isOpen={openDialog === 'form'}
        onClose={() => setOpenDialog(null)}
        onInsert={handleDialogInsert}
      />
      <SingleFormDialog
        isOpen={openDialog === 'singleForm'}
        onClose={() => setOpenDialog(null)}
        onInsert={handleDialogInsert}
      />
      <InteractiveFlowDialog
        isOpen={openDialog === 'interactiveFlow'}
        onClose={() => setOpenDialog(null)}
        onInsert={handleDialogInsert}
      />
      <GoogleReviewsDialog
        isOpen={openDialog === 'googleReviews'}
        onClose={() => setOpenDialog(null)}
        onInsert={handleDialogInsert}
      />
      <TableDialog
        isOpen={openDialog === 'table'}
        onClose={() => setOpenDialog(null)}
        onInsert={handleDialogInsert}
      />
    </div>
  );
});

RichTextEditor.displayName = 'RichTextEditor';

export default RichTextEditor;
